import React, { useCallback, useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { useDispatch } from "react-redux";

import {
  ADD_ACCOUNTS_ERROR_MESSAGE,
  AGGREGATOR_TYPE_FINICITY,
  EXISTING_ACCOUNT_BUTTON_TEXT,
  FETCH_ACCOUNTS_ERROR_MESSAGE,
  NO_ACCOUNT_BUTTON_TEXT,
  PLAID_EVENT_TYPES,
} from "../../../constants";

import {
  fetchPlaidToken,
  removePlaidTokenV2,
  createPlaidEvent,
} from "../../../state/actions";

import Sentry from "../../../api/sentry";
import { GET_APP_INFO_AND_ACCOUNTS } from "../../../api/gql";
import { ADD_FINICITY_ACCOUNTS, ADD_PLAID_ACCOUNTS } from "./gql";

import { FinicityConnect } from "../../linkbank/components";
import LinkBankPlaid from "../../linkbank/LinkBankPlaid";
import Loading from "../../common/Loading";
import withQuery from "../wrappers/gql/withQuery";
import { uriHasOAuthLogin, uriHasOAuthStateId } from "../../../utilities/userInfo";

const LinkBankForm = ({
  setErrorNetwork = () => {},
  data: linkBankInfo,
  refetchQuery: fetchAccounts,
}) => {
  const dispatch = useDispatch();
  const [addFinicityAccounts, {
    loading: finicityAccountUpdatesLoading,
  }] = useMutation(ADD_FINICITY_ACCOUNTS);
  const [addPlaidAccounts, {
    loading: plaidAccountUpdatesLoading,
  }] = useMutation(ADD_PLAID_ACCOUNTS);

  const { getBankAccounts, getApplicationInfo, finicityConnect } = linkBankInfo;
  const { email } = getApplicationInfo;
  const { accountsByLogin: totalAccounts, aggregatorOverride: aggregatorType } = getBankAccounts;
  const accountsByLogin = totalAccounts || [];
  const existingAccounts = accountsByLogin.length > 0;
  const { customer_id: customerId, link } = finicityConnect;

  const [refreshAccountsLoading, setRefreshAccountsLoading] = useState(false);

  const [fromFinicityConnect, setFromFinicityConnect] = useState(false);
  const [plaidToken, setPlaidToken] = useState(null);
  const [plaidLinkFromOauth, setPlaidLinkFromOauth ] = useState(false);

  const [finicityLink, setFinicityLink] = useState(null);

  const _initializeForm = useCallback(async () => {

    if(aggregatorType === AGGREGATOR_TYPE_FINICITY && !uriHasOAuthStateId()) {
      setFinicityLink(link);
      return;
    }

    const { fromOauthRedirect, linkToken } = await dispatch(fetchPlaidToken({
      userId: email,
      fetchFromDb: uriHasOAuthLogin(),
    }));

    setPlaidLinkFromOauth(fromOauthRedirect);
    setPlaidToken(linkToken);

  }, [dispatch, email]);

  useEffect(() => {
    _initializeForm();
  }, []);

  const refreshAccounts = async () => {
    setRefreshAccountsLoading(true);
    try {
      await fetchAccounts();
    } catch (err) {
      Sentry.captureException(err);
      setErrorNetwork(FETCH_ACCOUNTS_ERROR_MESSAGE);
    } finally {
      setRefreshAccountsLoading(false);
    }
  };

  // Helper Functions Finicity
  const onFormSubmissionFinicity = async () => {
    setErrorNetwork(null);
    try {
      await addFinicityAccounts();
    } catch (err) {
      Sentry.captureException(err);
      setErrorNetwork(ADD_ACCOUNTS_ERROR_MESSAGE);
      return;
    }

    await refreshAccounts();
  };

  const openPlaidExperience = async () => {
    setErrorNetwork(null);
    const { fromOauthRedirect, linkToken } = await dispatch(fetchPlaidToken({
      userId: email,
    }));

    setPlaidToken(linkToken);
    setPlaidLinkFromOauth(fromOauthRedirect);
    setFromFinicityConnect(true);
  };

  // Helper Functions Plaid
  const closePlaid = (closeOptions) => {
    const { deletePlaidLinkInDb } = closeOptions || {};

    setPlaidToken(null);
    dispatch(removePlaidTokenV2({ removeFromDb: deletePlaidLinkInDb, plaidLinkToken: plaidToken }));

    setPlaidLinkFromOauth(false);
    setFromFinicityConnect(false);
    _initializeForm();
  };

  const onFormSubmissionPlaid = async (token, metadata) => {
    const { institution } = metadata;

    setFromFinicityConnect(false);
    setPlaidLinkFromOauth(false);

    try {
      await addPlaidAccounts({
        variables: {
          publicToken: token,
          institution,
        },
      });
    } catch (err) {
      Sentry.captureException(err);
      setErrorNetwork(ADD_ACCOUNTS_ERROR_MESSAGE);
      return;
    }

    closePlaid();
    await refreshAccounts();
  };

  const handlePlaidOnEvent = async (eventType, metadata) => {
    const { error_message: errorMessage } = metadata;

    // TODO: Potentially add tracking for all other plaid events that happen?
    // Look into PLAID_EVENT_TYPES for potential things to track

    if(eventType === PLAID_EVENT_TYPES.ERROR || eventType === PLAID_EVENT_TYPES.EXIT){
      closePlaid({ deletePlaidLinkInDb: true });
      if(errorMessage)
        dispatch(createPlaidEvent(metadata));

    }

    if(eventType === PLAID_EVENT_TYPES.HANDOFF)
      closePlaid();

  };

  const loadingState = finicityAccountUpdatesLoading ||
    plaidAccountUpdatesLoading ||
    refreshAccountsLoading;
  const buttonText = existingAccounts ? EXISTING_ACCOUNT_BUTTON_TEXT : NO_ACCOUNT_BUTTON_TEXT;

  return (
    <>
      {loadingState && <Loading showBackdrop />}
      { plaidToken ?
        <LinkBankPlaid
          linkToken={plaidToken}
          fromOauthRedirect={plaidLinkFromOauth}
          fromFinicityConnect={fromFinicityConnect}
          onSuccess={onFormSubmissionPlaid}
          onEvent={handlePlaidOnEvent}
          text={buttonText}
          boxStyle={!!existingAccounts}
          continueStyle
        /> :
        <FinicityConnect
          linkURL={finicityLink}
          finicityCustomerId={customerId}
          email={email}
          disabled={loadingState}
          allowShowMore
          showMoreAccounts={openPlaidExperience}
          onSuccess={onFormSubmissionFinicity}
          onCancel={onFormSubmissionFinicity}
          buttonClassName={
            existingAccounts ?
              "bg-green-primary addAccBtn w-full py-2 text-white text-lg rounded cursor-pointer" :
              "continueButton-noMargin rounded mt-4"
          }
          buttonText={buttonText}
        />
      }
    </>
  );
};

export default withQuery(GET_APP_INFO_AND_ACCOUNTS)(LinkBankForm);